import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { myToaster } from '@interstellar-component'

import { myToasterFromApi } from '@src/services/Helper'
import { useApp } from '@src/AppContext'
import { withAgreementMessage } from '@src/pages/Company/components/agreementStatus'
import { RegisterCompanyService } from './service'

const RegisterCompanyInfoContext = createContext()

// The created company's id is persisted so a draft can be resumed after a
// reload / re-login (enrollment_step drives which wizard step to show).
// Exported so the post-signing route (SigningComplete) can drop it once the
// enrollment is finished — otherwise a completed company lingers in localStorage
// and the next "New company" resumes it at its last step.
export const COMPANY_ID_STORAGE_KEY = 'register_company_id'

const toOption = (row) => ({ label: row.name ?? row.code, value: row.name ?? row.code, id: row.id })

function RegisterCompanyInfoProvider({ children }) {
  const [companyData, setCompanyData] = useState({})
  const [companyId, setCompanyIdState] = useState(
    () => localStorage.getItem(COMPANY_ID_STORAGE_KEY) || null
  )

  // ─── Option lists ──────────────────────────────────────────────────────────
  const [businessCategories, setBusinessCategories] = useState([])
  const [industries, setIndustries] = useState([])
  const [employeeRanges, setEmployeeRanges] = useState([])
  const [provinces, setProvinces] = useState([])
  const [cities, setCities] = useState([])
  const [districts, setDistricts] = useState([])
  const [subdistricts, setSubdistricts] = useState([])
  const [postalCodes, setPostalCodes] = useState([])

  useEffect(() => {
    Promise.all([
      RegisterCompanyService.businessCategories(),
      RegisterCompanyService.industries(),
      RegisterCompanyService.employeeRanges(),
      RegisterCompanyService.provinces(),
    ])
      .then(([categories, industriesRes, ranges, provincesRes]) => {
        setBusinessCategories((categories?.data || []).map(toOption))
        setIndustries((industriesRes?.data || []).map(toOption))
        setEmployeeRanges((ranges?.data || []).map(toOption))
        setProvinces((provincesRes?.data || []).map(toOption))
      })
      .catch(myToaster)
  }, [])

  // Cascading loaders — each clears its descendants so stale options never
  // survive a parent change.
  const loadCities = useCallback(async (provinceId) => {
    setCities([])
    setDistricts([])
    setSubdistricts([])
    setPostalCodes([])
    if (!provinceId) return
    await RegisterCompanyService.cities(provinceId)
      .then((res) => setCities((res?.data || []).map(toOption)))
      .catch(myToaster)
  }, [])

  const loadDistricts = useCallback(async (cityId) => {
    setDistricts([])
    setSubdistricts([])
    setPostalCodes([])
    if (!cityId) return
    await RegisterCompanyService.districts(cityId)
      .then((res) => setDistricts((res?.data || []).map(toOption)))
      .catch(myToaster)
  }, [])

  const loadSubdistricts = useCallback(async (districtId) => {
    setSubdistricts([])
    setPostalCodes([])
    if (!districtId) return
    await RegisterCompanyService.subdistricts(districtId)
      .then((res) => setSubdistricts((res?.data || []).map(toOption)))
      .catch(myToaster)
  }, [])

  const loadPostalCodes = useCallback(async (subdistrictId) => {
    setPostalCodes([])
    if (!subdistrictId) return
    await RegisterCompanyService.postalCodes(subdistrictId)
      .then((res) =>
        setPostalCodes((res?.data || []).map((row) => ({ label: row.code, value: row.code, id: row.id })))
      )
      .catch(myToaster)
  }, [])

  // ─── Company id persistence ────────────────────────────────────────────────
  const { getSession } = useApp()
  const setCompanyId = useCallback((id) => {
    setCompanyIdState(id)
    if (id) localStorage.setItem(COMPANY_ID_STORAGE_KEY, id)
    else localStorage.removeItem(COMPANY_ID_STORAGE_KEY)
  }, [])

  // ─── Enrollment steps ──────────────────────────────────────────────────────
  const submitStep1 = useCallback(
    async (formData, { draft = false } = {}) => {
      // Resume: the company already exists (created earlier as a draft), so
      // UPDATE step 1 in place. Re-creating would 409 on the duplicate legal
      // name and never advance the enrollment. New enrollment → create.
      const request = companyId
        ? RegisterCompanyService.updateStep1(companyId, formData, { draft })
        : RegisterCompanyService.createCompany(formData, { draft })
      const result = await request
        .then((res) => {
          myToaster(res)
          return res
        })
        .catch((e) => {
          // Surface the field-specific validation message (e.g. "Company address
          // must not exceed 275 characters") instead of the generic top-level
          // "Validation error" — the BE already returns it in errors[] (Everest
          // #86ey2n7a2).
          myToasterFromApi(e)
          throw e
        })
      const saved = result?.data
      if (saved?.id) {
        setCompanyId(saved.id)
        setCompanyData(saved)
      }
      // A NEW company changes the session's company_context (count 0 → 1). Refresh
      // it now: otherwise /company still sees count 0 at the end of enrollment and
      // its redirect fires before the route-change refetch lands, dropping the user
      // back into an empty wizard ("Back to the company page" after signing).
      if (!companyId && saved?.id) getSession()
      return saved
    },
    [companyId, setCompanyId, getSession]
  )

  const submitStep2 = useCallback(
    async (formData, { draft = false } = {}) => {
      if (!companyId) throw new Error('No company in progress')
      const result = await RegisterCompanyService.enrollStep2(companyId, formData, { draft })
        .then((res) => {
          myToaster(res)
          return res
        })
        .catch((e) => {
          // Surface the field-specific validation message (e.g. "Company address
          // must not exceed 275 characters") instead of the generic top-level
          // "Validation error" — the BE already returns it in errors[] (Everest
          // #86ey2n7a2).
          myToasterFromApi(e)
          throw e
        })
      if (result?.data) setCompanyData(result.data)
      return result?.data
    },
    [companyId]
  )

  const submitStep3 = useCallback(
    async (formData, { draft = false } = {}) => {
      if (!companyId) throw new Error('No company in progress')
      const result = await RegisterCompanyService.enrollStep3(companyId, formData, { draft })
        .then((res) => {
          myToaster(res)
          return res
        })
        .catch((e) => {
          // Surface the field-specific validation message (e.g. "Company address
          // must not exceed 275 characters") instead of the generic top-level
          // "Validation error" — the BE already returns it in errors[] (Everest
          // #86ey2n7a2).
          myToasterFromApi(e)
          throw e
        })
      if (result?.data) setCompanyData(result.data)
      return result?.data
    },
    [companyId]
  )

  /**
   * Step 4 — generate the DocuSign envelope + embedded signing URL. Returns the
   * inner data ({ signing_url, ... }); the Form redirects the browser to it and
   * the dedicated SigningComplete route handles the post-signing return.
   */
  const submitStep4 = useCallback(async () => {
    if (!companyId) throw new Error('No company in progress')
    const result = await RegisterCompanyService.enrollStep4(companyId)
      .then((res) => res)
      .catch((e) => {
        // 503 docusign_clik_signer_not_configured / 502 docusign_unavailable get
        // a plain sentence instead of the raw (config-naming) message.
        myToasterFromApi(withAgreementMessage(e))
        throw e
      })
    return result?.data
  }, [companyId])

  /** Resume a draft: returns the enrollment detail (with enrollment_step) or null. */
  const resumeEnrollment = useCallback(async () => {
    if (!companyId) return null
    try {
      const result = await RegisterCompanyService.getEnrollment(companyId)
      const company = result?.data
      // A finished enrollment (agreement signed → tim-SAW review) is no longer a
      // resumable draft. Clearing here is the authoritative guard: it also closes
      // the window where the user leaves the signing page before the DocuSign
      // webhook flips enrollment_status to "completed" — the next "New company"
      // re-checks the live status and drops the stale id instead of resuming.
      if (!company || company.enrollment_status === 'completed') {
        setCompanyId(null)
        return null
      }
      setCompanyData(company)
      return company
    } catch {
      // Stale id (deleted company / lost access) — start fresh.
      setCompanyId(null)
      return null
    }
  }, [companyId, setCompanyId])

  /** Wizard finished (or abandoned for the company list) — drop the stored id. */
  const clearEnrollment = useCallback(() => {
    setCompanyId(null)
    setCompanyData({})
  }, [setCompanyId])

  const contextValue = useMemo(
    () => ({
      companyData,
      setCompanyData,
      companyId,
      submitStep1,
      submitStep2,
      submitStep3,
      submitStep4,
      resumeEnrollment,
      clearEnrollment,
      options: {
        businessCategories,
        industries,
        employeeRanges,
        provinces,
        cities,
        districts,
        subdistricts,
        postalCodes,
      },
      loadCities,
      loadDistricts,
      loadSubdistricts,
      loadPostalCodes,
    }),
    [
      companyData,
      companyId,
      submitStep1,
      submitStep2,
      submitStep3,
      submitStep4,
      resumeEnrollment,
      clearEnrollment,
      businessCategories,
      industries,
      employeeRanges,
      provinces,
      cities,
      districts,
      subdistricts,
      postalCodes,
      loadCities,
      loadDistricts,
      loadSubdistricts,
      loadPostalCodes,
    ]
  )

  return (
    <RegisterCompanyInfoContext.Provider value={contextValue}>
      {children}
    </RegisterCompanyInfoContext.Provider>
  )
}

const useRegisterCompanyInfo = () => {
  const context = useContext(RegisterCompanyInfoContext)
  if (context === undefined) {
    throw new Error('useRegisterCompanyInfo must be used within a RegisterCompanyInfoProvider')
  }
  return context
}

export { RegisterCompanyInfoProvider, useRegisterCompanyInfo }
